// apps/client/src/pages/reports/ReferralPartnerPage.tsx
// REFERRAL PARTNER REPORT - Analytics view of existing referrer data
// NOT a CRM. Read-only view of referral activity from commission files.

import React, { useState, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Users, DollarSign, UserCheck, UserX, ArrowUpDown } from 'lucide-react';
import StatCard from '../../components/common/StatCard';
import { PageHeader } from '../../components/common/PageHeader';
import { formatCurrency } from '../../utils/formatters';
import { useReferralPartners, ReferralPartner } from '../../hooks/useReferralPartners';
import { useCompanyDataYearRange } from '../../hooks/useCompanyDataYearRange';
import { resolveLoanBookSelection } from '../../utils/loanBookResolution';
import type { ResolvedLoanBook } from '@valutten/shared-schemas';

type SortField = 'referrer' | 'loanCount' | 'totalUpfrontPaidCents' | 'totalTrailPaidCents' | 'lastReferralDate';
type SortDirection = 'asc' | 'desc';

const ReferralPartnerPage: React.FC = () => {
  const { userData, loanBooks = [] } = useOutletContext<any>();
  const hasEntityLoanBooks = useMemo(() =>
    (loanBooks as ResolvedLoanBook[]).some((lb: ResolvedLoanBook) => lb.type === 'entity'),
    [loanBooks]
  );
  const [selectedLoanBook, setSelectedLoanBook] = useState('all');
  const resolvedParams = useMemo(
    () => resolveLoanBookSelection(selectedLoanBook, loanBooks),
    [selectedLoanBook, loanBooks]
  );

  const { earliestYear, latestYear } = useCompanyDataYearRange({
    companyId: userData?.companyId,
  });

  // Period filter state
  const [periodType, setPeriodType] = useState<'all' | 'calendar' | 'financial'>('all');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Sort state
  const [sortField, setSortField] = useState<SortField>('loanCount');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // View state
  const [showInactive, setShowInactive] = useState(false);

  // Calculate period bounds
  const { periodStart, periodEnd } = useMemo(() => {
    if (periodType === 'all') return { periodStart: undefined, periodEnd: undefined };
    if (periodType === 'calendar') {
      return { periodStart: `${selectedYear}-01`, periodEnd: `${selectedYear}-12` };
    }
    // Financial year: July Yr → June Yr+1
    return { periodStart: `${selectedYear}-07`, periodEnd: `${selectedYear + 1}-06` };
  }, [periodType, selectedYear]);

  const { partners, totals, loading, error } = useReferralPartners({
    companyId: userData?.companyId || '',
    periodStart,
    periodEnd,
    entityCode: resolvedParams.entityCode,
  });

  // Year options
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const start = earliestYear ?? (currentYear - 4);
    const end = Math.max(latestYear ?? currentYear, currentYear);
    const years = [];
    for (let y = end; y >= start; y--) years.push(y);
    return years;
  }, [earliestYear, latestYear]);

  // Sort and filter partners
  const displayedPartners = useMemo(() => {
    let filtered = showInactive ? partners : partners.filter((p) => !p.isInactive);

    return [...filtered].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      const cmp = typeof aVal === 'string'
        ? aVal.localeCompare(bVal as string)
        : (aVal as number) - (bVal as number);
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [partners, sortField, sortDirection, showInactive]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const SortHeader: React.FC<{ field: SortField; label: string; className?: string }> = ({ field, label, className }) => (
    <th
      className={`px-6 py-4 text-sm font-semibold text-gray-300 cursor-pointer hover:text-white transition-colors ${className || ''}`}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortField === field && (
          <ArrowUpDown className={`w-3 h-3 ${sortDirection === 'asc' ? 'rotate-180' : ''}`} />
        )}
      </div>
    </th>
  );

  if (loading) {
    return <div className="p-8 text-center text-white">Loading referral partner report...</div>;
  }

  if (error) {
    return (
      <div className="p-8 text-center text-red-500">
        Error loading report: {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Users}
        title="Referral Partners"
        subtitle="Analytics view of referral activity from commission data"
      />

      {/* Filter Controls */}
      <div className="flex gap-4 mb-8">
        <div className="flex flex-col">
          <label className="text-sm text-gray-400 mb-2">Period</label>
          <div className="flex gap-2">
            {(['all', 'calendar', 'financial'] as const).map((type) => (
              <button
                key={type}
                className={`px-4 py-2 rounded ${
                  periodType === type ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400'
                }`}
                onClick={() => setPeriodType(type)}
              >
                {type === 'all' ? 'All Time' : type === 'calendar' ? 'Calendar Year' : 'Financial Year'}
              </button>
            ))}
          </div>
        </div>

        {periodType !== 'all' && (
          <div className="flex flex-col">
            <label className="text-sm text-gray-400 mb-2">Year</label>
            <select
              className="bg-gray-800 text-white px-4 py-2 rounded border border-gray-700"
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        )}

        {hasEntityLoanBooks && (
          <div className="flex flex-col">
            <label className="text-sm text-gray-400 mb-2">Loan Book</label>
            <select
              className="bg-gray-800 text-white px-4 py-2 rounded border border-gray-700"
              value={selectedLoanBook}
              onChange={(e) => setSelectedLoanBook(e.target.value)}
            >
              <option value="all">All Loan Books</option>
              {(loanBooks as ResolvedLoanBook[]).map((lb: ResolvedLoanBook) => (
                <option key={lb.id} value={lb.id}>{lb.name}</option>
              ))}
            </select>
          </div>
        )}

        <div className="flex flex-col">
          <label className="text-sm text-gray-400 mb-2">View</label>
          <button
            className={`px-4 py-2 rounded ${
              showInactive ? 'bg-orange-600 text-white' : 'bg-gray-800 text-gray-400'
            }`}
            onClick={() => setShowInactive(!showInactive)}
          >
            {showInactive ? 'Showing All' : 'Active Only'}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {totals && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Partners"
            value={totals.totalPartners.toLocaleString()}
            subtitle={`${totals.activePartners} active, ${totals.inactivePartners} inactive`}
            icon={<Users size={24} />}
          />
          <StatCard
            title="Total Referral Spend"
            value={formatCurrency(totals.totalSpentCents)}
            subtitle="Upfront + trail paid to referrers"
            icon={<DollarSign size={24} />}
          />
          <StatCard
            title="Average Per Partner"
            value={formatCurrency(totals.averagePerPartnerCents)}
            subtitle="Total spend / partners"
            icon={<UserCheck size={24} />}
          />
          <StatCard
            title="Inactive Partners"
            value={totals.inactivePartners.toLocaleString()}
            subtitle="No referrals in 12+ months"
            icon={<UserX size={24} />}
          />
        </div>
      )}

      {/* Partners Table */}
      <div className="bg-gray-800 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-900">
            <tr>
              <SortHeader field="referrer" label="Referral Partner" className="text-left" />
              <SortHeader field="loanCount" label="Loans" className="text-right" />
              <SortHeader field="totalUpfrontPaidCents" label="Upfront Paid" className="text-right" />
              <SortHeader field="totalTrailPaidCents" label="Trail Paid" className="text-right" />
              <th className="px-6 py-4 text-right text-sm font-semibold text-gray-300">Total Paid</th>
              <th className="px-6 py-4 text-right text-sm font-semibold text-gray-300">First Referral</th>
              <SortHeader field="lastReferralDate" label="Last Activity" className="text-right" />
              <th className="px-6 py-4 text-center text-sm font-semibold text-gray-300">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {displayedPartners.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-8 text-center text-gray-400">
                  {partners.length === 0
                    ? 'No referral partner data found in commission files'
                    : 'No active referral partners (toggle "Showing All" to see inactive)'}
                </td>
              </tr>
            ) : (
              displayedPartners.map((partner, index) => (
                <tr key={partner.referrer || index} className="hover:bg-gray-750 transition-colors">
                  <td className="px-6 py-4 text-white font-medium">{partner.referrer}</td>
                  <td className="px-6 py-4 text-right text-white">{partner.loanCount}</td>
                  <td className="px-6 py-4 text-right text-white">
                    {formatCurrency(partner.totalUpfrontPaidCents)}
                  </td>
                  <td className="px-6 py-4 text-right text-white">
                    {formatCurrency(partner.totalTrailPaidCents)}
                  </td>
                  <td className="px-6 py-4 text-right text-white font-semibold">
                    {formatCurrency(partner.totalUpfrontPaidCents + partner.totalTrailPaidCents)}
                  </td>
                  <td className="px-6 py-4 text-right text-gray-400 text-sm">
                    {partner.firstReferralDate || '—'}
                  </td>
                  <td className="px-6 py-4 text-right text-gray-400 text-sm">
                    {partner.lastReferralDate || '—'}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${
                      partner.isInactive
                        ? 'bg-red-500/20 text-red-400'
                        : 'bg-green-500/20 text-green-400'
                    }`}>
                      {partner.isInactive ? 'Inactive' : 'Active'}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Methodology Note */}
      <div className="mt-8 p-4 bg-gray-800 rounded-lg border border-gray-700">
        <h3 className="text-sm font-semibold text-gray-300 mb-2">About This Report</h3>
        <p className="text-sm text-gray-400">
          This report surfaces referral partner data already present in your commission files.
          Upfront and trail payments to referrers are tracked via the referrer_upfront_deduction
          and referrer_trail_deduction payment types. Partners with no activity in the last 12 months
          are flagged as inactive — these may represent retention opportunities.
          This is an analytics view only, not a CRM or payment management system.
        </p>
      </div>
    </div>
  );
};

export default ReferralPartnerPage;
